import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type AdminIntensityLevel,
  useUpdateIntensityLevel,
} from "@/hooks/use-admin-intensity-levels";

const schema = z.object({
  label: z.string().min(1, "Label is required").max(60),
  description: z.string().max(500).optional(),
  color: z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Use a hex code like #6E47A4"),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: AdminIntensityLevel;
}

export function IntensityLevelEditDialog({ open, onOpenChange, level }: Props) {
  const updateMut = useUpdateIntensityLevel();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: level.label,
      description: level.description ?? "",
      color: level.color,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        label: level.label,
        description: level.description ?? "",
        color: level.color,
      });
    }
  }, [open, level, form]);

  function onSubmit(values: FormValues) {
    updateMut.mutate(
      {
        id: level.id,
        label: values.label,
        description: values.description?.trim() ? values.description : null,
        color: values.color,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const colorPreview = form.watch("color");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Level {level.level}</DialogTitle>
          <DialogDescription>
            Tune how this intensity level appears in the dashboard. The level number is fixed by the
            system design (8 levels) and can&apos;t be changed.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label</FormLabel>
                  <FormControl>
                    <Input placeholder={`Level ${level.level}`} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder="Optional notes for RMs on when this level applies"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-block h-9 w-9 shrink-0 rounded-md border"
                      style={{ backgroundColor: colorPreview }}
                      aria-hidden
                    />
                    <FormControl>
                      <Input placeholder="#6E47A4" {...field} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMut.isPending}>
                {updateMut.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
